import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { ArrowLeft, ArrowRight, Check, Sparkles, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

import spaceDev from "../../assets/space-dev.png";

const BaseUrl = "https://api.zulfahgroup.com/api/v1";

const Index = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [form, setForm] = useState({
    surname: "",
    othernames: "",
    phoneNumber: "",
    email: "",
    gender: "",
    programme: "",
    programmeType: "",
    devExperience: "",
    infoMedium: "",
  });

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const steps = [
    {
      number: 1,
      label: "About You",
      description: "Let's get to know you",
    },
    {
      number: 2,
      label: "Your Journey",
      description: "Tell us where you are",
    },
    {
      number: 3,
      label: "Your Programme",
      description: "Choose your direction",
    },
  ];

  const progress = (step / steps.length) * 100;

  const validateStep = () => {
    if (step === 1) {
      if (
        !form.surname ||
        !form.othernames ||
        !form.phoneNumber ||
        !form.email ||
        !form.gender
      ) {
        toast.error("Please complete all the fields before continuing.");
        return false;
      }
    }

    if (step === 2) {
      if (!form.devExperience || !form.infoMedium) {
        toast.error("Please complete this section before continuing.");
        return false;
      }
    }

    if (step === 3) {
      if (!form.programme || !form.programmeType) {
        toast.error("Please select your programme and preferred schedule.");
        return false;
      }
    }

    return true;
  };

  const nextStep = () => {
    if (!validateStep()) return;

    if (step < 3) {
      setStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const previousStep = () => {
    if (step > 1) {
      setStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep()) return;

    setIsSubmitted(true);

    try {
      const { data } = await axios.post(`${BaseUrl}/application/create`, {
        surname: form.surname,
        othernames: form.othernames,
        address: "default address",
        sex: form.gender,
        phone: form.phoneNumber,
        dob: "2021-01-01",
        country: "default country",
        city: "default city",
        current_location: "default address",
        state: "default state",
        highest_qualification: "others",
        years_of_programming: form.devExperience,
        programming_language: "Nil",
        email: form.email,
        where_you_heard_about_us: form.infoMedium,
        available_in_six_month: true,
        programme_type: form.programmeType,
        programme: form.programme,
        payment_option: "SIP",
      });

      setIsSubmitted(false);

      await Swal.fire({
        title: "You're officially on your way 🚀",
        text:
          data?.message ||
          "Your application has been submitted successfully.",
        icon: "success",
        confirmButtonColor: "#59b5cd",
        confirmButtonText: "Continue",
        background: "#ffffff",
        customClass: {
          popup: "rounded-3xl",
        },
      });

      navigate("/");
    } catch (error) {
      setIsSubmitted(false);

      toast.error(
        error?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#f7fafb] overflow-hidden">

      {/* =====================================================
          AMBIENT BACKGROUND
      ====================================================== */}

      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#59b5cd]/10 blur-[120px]" />
        <div className="absolute top-[35%] -right-40 w-[450px] h-[450px] rounded-full bg-[#ecb32c]/10 blur-[120px]" />
        <div className="absolute bottom-0 left-[35%] w-[400px] h-[400px] rounded-full bg-[#cf375d]/5 blur-[120px]" />
      </div>

      {/* =====================================================
          TOP BAR
      ====================================================== */}

      <header className="relative z-20 border-b border-black/5 bg-white/80 backdrop-blur-xl">

        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">

          <button
            onClick={() => navigate(-1)}
            className="group flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-black transition"
          >
            <span className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center group-hover:-translate-x-1 transition">
              <ArrowLeft size={17} />
            </span>

            <span className="hidden sm:block">
              Back
            </span>
          </button>

          <div className="text-center">
            <p className="font-black tracking-tight text-lg">
              ZULFAH<span className="text-[#59b5cd]">.</span>
            </p>

            <p className="text-[9px] uppercase tracking-[0.25em] text-gray-400">
              Academy
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500">
            <ShieldCheck size={15} className="text-[#59b5cd]" />
            <span className="hidden sm:block">Secure application</span>
          </div>

        </div>

      </header>


      {/* =====================================================
          HERO
      ====================================================== */}

      <main className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-16">

        <div className="grid lg:grid-cols-[0.85fr_1.5fr] gap-10 lg:gap-20 items-start">

          {/* =================================================
              LEFT EXPERIENCE PANEL
          ================================================= */}

          <aside className="lg:sticky lg:top-10">

            <div className="relative overflow-hidden rounded-[2rem] bg-[#07151c] text-white p-7 sm:p-10 min-h-[500px] flex flex-col justify-between">

              {/* Decorative gradients */}
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#59b5cd]/30 blur-[80px] rounded-full" />
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#ecb32c]/20 blur-[80px] rounded-full" />

              {/* Image */}
              <motion.img
                src={spaceDev}
                alt="Build your future with Zulfah Academy"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 w-full max-w-[280px] mx-auto object-contain"
              />

              <div className="relative z-10">

                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-xs text-white/70 mb-5">
                  <Sparkles size={13} className="text-[#ecb32c]" />
                  Your next chapter starts here
                </div>

                <h1 className="text-3xl sm:text-4xl font-black leading-tight">
                  Don't just learn tech.
                  <span className="block text-[#59b5cd]">
                    Build your future.
                  </span>
                </h1>

                <p className="mt-5 text-sm leading-7 text-white/60">
                  Join a community of builders, innovators and problem-solvers
                  learning the skills shaping tomorrow.
                </p>

                <div className="mt-7 flex flex-wrap gap-2">
                  {[
                    "Practical Learning",
                    "Expert Mentors",
                    "Real Projects",
                  ].map((item) => (
                    <span
                      key={item}
                      className="px-3 py-2 rounded-full bg-white/5 border border-white/10 text-[11px] text-white/70"
                    >
                      {item}
                    </span>
                  ))}
                </div>

              </div>

            </div>

          </aside>


          {/* =================================================
              APPLICATION AREA
          ================================================= */}

          <section>

            {/* Heading */}

            <div className="mb-10">

              <p className="text-[#59b5cd] text-xs font-bold uppercase tracking-[0.3em]">
                Admissions
              </p>

              <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#07151c]">
                Start your
                <span className="text-[#59b5cd]"> journey.</span>
              </h2>

              <p className="mt-4 text-gray-500 max-w-xl leading-7">
                Tell us a little about yourself and where you want your
                technology journey to take you.
              </p>

            </div>


            {/* =================================================
                STEPPER
            ================================================== */}

            <div className="mb-10">

              <div className="flex items-center justify-between">

                {steps.map((item, index) => {

                  const active = step === item.number;
                  const completed = step > item.number;

                  return (
                    <div
                      key={item.number}
                      className="flex items-center flex-1 last:flex-none"
                    >

                      <div className="flex items-center gap-3">

                        <div
                          className={`
                            w-10 h-10 rounded-full flex items-center justify-center
                            font-bold text-sm transition-all duration-500
                            ${
                              active
                                ? "bg-[#59b5cd] text-white shadow-lg shadow-[#59b5cd]/25 scale-110"
                                : completed
                                ? "bg-[#07151c] text-white"
                                : "bg-white border border-gray-200 text-gray-400"
                            }
                          `}
                        >
                          {completed ? (
                            <Check size={17} />
                          ) : (
                            item.number
                          )}
                        </div>

                        <div className="hidden sm:block">
                          <p
                            className={`text-xs font-bold ${
                              active
                                ? "text-gray-900"
                                : "text-gray-400"
                            }`}
                          >
                            {item.label}
                          </p>

                          <p className="text-[10px] text-gray-400 mt-1">
                            {item.description}
                          </p>
                        </div>

                      </div>

                      {index < steps.length - 1 && (
                        <div className="h-[1px] flex-1 mx-3 sm:mx-5 bg-gray-200 overflow-hidden">
                          <motion.div
                            className="h-full bg-[#59b5cd]"
                            initial={{ width: 0 }}
                            animate={{
                              width: step > item.number ? "100%" : "0%",
                            }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                      )}

                    </div>
                  );
                })}

              </div>

              {/* progress */}
              <div className="mt-6 h-1 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#59b5cd] to-[#ecb32c]"
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>

            </div>


            {/* =================================================
                FORM CARD
            ================================================== */}

            <form
              onSubmit={handleSubmit}
              className="bg-white border border-gray-100 rounded-[2rem] shadow-[0_20px_70px_rgba(7,21,28,0.07)] overflow-hidden"
            >

              <div className="p-6 sm:p-10">

                <AnimatePresence mode="wait">

                  {/* =================================================
                      STEP 1
                  ================================================== */}

                  {step === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.3 }}
                    >

                      <FormHeading
                        eyebrow="01 — About You"
                        title="Let's start with you."
                        description="We need a few basic details to create your application."
                      />

                      <div className="grid sm:grid-cols-2 gap-5 mt-8">

                        <Input
                          label="Surname"
                          placeholder="e.g. Ajao"
                          value={form.surname}
                          onChange={(e) =>
                            updateField("surname", e.target.value)
                          }
                        />

                        <Input
                          label="Other names"
                          placeholder="e.g. Abayomi"
                          value={form.othernames}
                          onChange={(e) =>
                            updateField("othernames", e.target.value)
                          }
                        />

                        <Input
                          label="Phone number"
                          type="tel"
                          placeholder="+234 800 000 0000"
                          value={form.phoneNumber}
                          onChange={(e) =>
                            updateField("phoneNumber", e.target.value)
                          }
                        />

                        <Input
                          label="Email address"
                          type="email"
                          placeholder="you@example.com"
                          value={form.email}
                          onChange={(e) =>
                            updateField("email", e.target.value)
                          }
                        />

                        <Select
                          label="Gender"
                          value={form.gender}
                          onChange={(e) =>
                            updateField("gender", e.target.value)
                          }
                          options={[
                            ["", "Select gender"],
                            ["Male", "Male"],
                            ["Female", "Female"],
                          ]}
                        />

                      </div>

                    </motion.div>
                  )}


                  {/* =================================================
                      STEP 2
                  ================================================== */}

                  {step === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.3 }}
                    >

                      <FormHeading
                        eyebrow="02 — Your Journey"
                        title="Where are you starting from?"
                        description="There is no wrong answer. Every great technology journey starts somewhere."
                      />

                      <div className="mt-8 space-y-6">

                        <div>

                          <label className="block text-sm font-bold text-gray-800 mb-3">
                            Your programming experience
                          </label>

                          <div className="grid sm:grid-cols-3 gap-3">

                            {[
                              ["0", "I'm a beginner", "Starting from zero"],
                              ["1-2years", "1–2 years", "I've built a few things"],
                              ["3+years", "3+ years", "I'm experienced"],
                            ].map(([value, title, description]) => (

                              <ChoiceCard
                                key={value}
                                selected={form.devExperience === value}
                                onClick={() =>
                                  updateField("devExperience", value)
                                }
                                title={title}
                                description={description}
                              />

                            ))}

                          </div>

                        </div>


                        <div>

                          <label className="block text-sm font-bold text-gray-800 mb-3">
                            How did you discover Zulfah?
                          </label>

                          <div className="grid sm:grid-cols-2 gap-3">

                            {[
                              ["facebook", "Facebook"],
                              ["linkedin", "LinkedIn"],
                              ["twitter", "Twitter / X"],
                              ["an-alumnus", "An alumnus"],
                              ["google-search", "Google Search"],
                            ].map(([value, label]) => (

                              <ChoiceCard
                                key={value}
                                selected={form.infoMedium === value}
                                onClick={() =>
                                  updateField("infoMedium", value)
                                }
                                title={label}
                              />

                            ))}

                          </div>

                        </div>

                      </div>

                    </motion.div>
                  )}


                  {/* =================================================
                      STEP 3
                  ================================================== */}

                  {step === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.3 }}
                    >

                      <FormHeading
                        eyebrow="03 — Your Direction"
                        title="Choose what you want to build."
                        description="Pick the programme that best matches where you want your technology journey to go."
                      />

                      <div className="grid sm:grid-cols-2 gap-4 mt-8">

                        {[
                          [
                            "Frontend",
                            "Frontend Development",
                            "Build beautiful, interactive digital experiences.",
                          ],
                          [
                            "Backend",
                            "Backend Development",
                            "Build powerful systems behind modern applications.",
                          ],
                          [
                            "Fullstack",
                            "Fullstack Development",
                            "Learn to build complete digital products.",
                          ],
                          [
                            "Cybersecurity",
                            "Cybersecurity",
                            "Protect systems, people and digital infrastructure.",
                          ],
                          [
                            "Data Engineering",
                            "Data Engineering",
                            "Build the systems that power data-driven decisions.",
                          ],
                          [
                            "Artificial Intelligence",
                            "Artificial Intelligence",
                            "Explore intelligent systems and the future of AI.",
                          ],
                        ].map(([value, title, description]) => (

                          <ProgrammeCard
                            key={value}
                            selected={form.programme === value}
                            onClick={() =>
                              updateField("programme", value)
                            }
                            title={title}
                            description={description}
                          />

                        ))}

                      </div>


                      <div className="mt-8">

                        <label className="block text-sm font-bold text-gray-800 mb-3">
                          Preferred learning schedule
                        </label>

                        <div className="grid sm:grid-cols-2 gap-4">

                          <ChoiceCard
                            selected={form.programmeType === "weekdays"}
                            onClick={() =>
                              updateField("programmeType", "weekdays")
                            }
                            title="Weekdays"
                            description="For learners who prefer weekday sessions."
                          />

                          <ChoiceCard
                            selected={form.programmeType === "weekends"}
                            onClick={() =>
                              updateField("programmeType", "weekends")
                            }
                            title="Weekends"
                            description="For learners balancing work or school."
                          />

                        </div>

                      </div>

                    </motion.div>
                  )}

                </AnimatePresence>

              </div>


              {/* =================================================
                  FORM FOOTER
              ================================================== */}

              <div className="border-t border-gray-100 bg-[#fafcfc] px-6 sm:px-10 py-5 flex flex-col sm:flex-row gap-4 justify-between">

                {step > 1 ? (
                  <button
                    type="button"
                    onClick={previousStep}
                    className="order-2 sm:order-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-gray-600 hover:bg-white transition"
                  >
                    <ArrowLeft size={17} />
                    Previous
                  </button>
                ) : (
                  <div />
                )}

                {step < 3 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="order-1 sm:order-2 flex items-center justify-center gap-2 px-7 py-3 rounded-xl bg-[#07151c] text-white font-semibold hover:bg-[#10252e] hover:scale-[1.02] transition shadow-lg"
                  >
                    Continue
                    <ArrowRight size={17} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitted}
                    className="order-1 sm:order-2 flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-[#59b5cd] to-[#ecb32c] text-white font-bold hover:scale-[1.02] transition shadow-lg disabled:opacity-60"
                  >
                    {isSubmitted ? (
                      "Submitting application..."
                    ) : (
                      <>
                        Submit Application
                        <ArrowRight size={17} />
                      </>
                    )}
                  </button>
                )}

              </div>

            </form>


            {/* Bottom reassurance */}

            <div className="mt-6 flex flex-wrap justify-center gap-6 text-xs text-gray-400">

              <span className="flex items-center gap-2">
                <Check size={14} className="text-[#59b5cd]" />
                Practical learning
              </span>

              <span className="flex items-center gap-2">
                <Check size={14} className="text-[#59b5cd]" />
                Expert mentorship
              </span>

              <span className="flex items-center gap-2">
                <Check size={14} className="text-[#59b5cd]" />
                Career-focused programmes
              </span>

            </div>

          </section>

        </div>

      </main>

    </div>
  );
};


/* ============================================================
   REUSABLE COMPONENTS
============================================================ */

const FormHeading = ({ eyebrow, title, description }) => (
  <div>
    <p className="text-[#59b5cd] text-[10px] font-black uppercase tracking-[0.25em]">
      {eyebrow}
    </p>

    <h3 className="text-2xl sm:text-3xl font-black text-[#07151c] mt-2">
      {title}
    </h3>

    <p className="text-gray-500 text-sm mt-2 max-w-lg leading-6">
      {description}
    </p>
  </div>
);


const Input = ({
  label,
  type = "text",
  placeholder,
  value,
  onChange,
}) => (
  <div>
    <label className="block text-sm font-bold text-gray-800 mb-2">
      {label}
    </label>

    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="
        w-full px-4 py-4 rounded-xl
        bg-[#f8fafb]
        border border-gray-200
        text-gray-900
        placeholder:text-gray-400
        outline-none
        transition-all duration-300
        focus:bg-white
        focus:border-[#59b5cd]
        focus:ring-4
        focus:ring-[#59b5cd]/10
      "
    />
  </div>
);


const Select = ({
  label,
  value,
  onChange,
  options,
}) => (
  <div>
    <label className="block text-sm font-bold text-gray-800 mb-2">
      {label}
    </label>

    <select
      value={value}
      onChange={onChange}
      className="
        w-full px-4 py-4 rounded-xl
        bg-[#f8fafb]
        border border-gray-200
        text-gray-900
        outline-none
        transition-all
        focus:bg-white
        focus:border-[#59b5cd]
        focus:ring-4
        focus:ring-[#59b5cd]/10
      "
    >
      {options.map(([value, label]) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  </div>
);


const ChoiceCard = ({
  selected,
  onClick,
  title,
  description,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      relative w-full text-left p-4 rounded-2xl border transition-all duration-300
      ${
        selected
          ? "border-[#59b5cd] bg-[#59b5cd]/5 shadow-md"
          : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
      }
    `}
  >
    <div className="flex items-start gap-3">

      <div
        className={`
          mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center shrink-0
          ${
            selected
              ? "border-[#59b5cd] bg-[#59b5cd]"
              : "border-gray-300"
          }
        `}
      >
        {selected && (
          <Check
            size={12}
            className="text-white"
          />
        )}
      </div>

      <div>
        <p className="font-bold text-sm text-gray-900">
          {title}
        </p>

        {description && (
          <p className="text-xs text-gray-500 mt-1 leading-5">
            {description}
          </p>
        )}
      </div>

    </div>
  </button>
);


const ProgrammeCard = ({
  selected,
  onClick,
  title,
  description,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      relative text-left p-5 rounded-2xl border transition-all duration-300 group
      ${
        selected
          ? "border-[#59b5cd] bg-[#59b5cd]/5 shadow-lg"
          : "border-gray-200 bg-white hover:-translate-y-1 hover:shadow-lg"
      }
    `}
  >

    <div className="flex justify-between items-start gap-4">

      <div>

        <h4 className="font-black text-gray-900">
          {title}
        </h4>

        <p className="text-xs text-gray-500 mt-2 leading-5">
          {description}
        </p>

      </div>

      <div
        className={`
          w-6 h-6 rounded-full border flex items-center justify-center shrink-0
          ${
            selected
              ? "bg-[#59b5cd] border-[#59b5cd]"
              : "border-gray-200"
          }
        `}
      >
        {selected && (
          <Check
            size={13}
            className="text-white"
          />
        )}
      </div>

    </div>

  </button>
);


export default Index;